/* eslint-disable react-refresh/only-export-components */
import { ReactNode, createContext, useContext, useEffect, useState } from 'react';

import { geoJsService } from '@/services/GeoJsService';

interface GeoContextValue {
  countryCode: string | null;
  /** The geo-IP detected country, before any manual region override. */
  detectedCountryCode: string | null;
  region: string | null;
  /** True when the visitor has manually chosen a region (overriding geo-IP). */
  isRegionOverridden: boolean;
  /** Manually override the detected region for this session; pass null to clear. */
  setRegionOverride: (countryCode: string | null) => void;
}

const GeoContext = createContext<GeoContextValue | null>(null);

const REGION_OVERRIDE_KEY = 'astron:region-override';

function readStoredOverride(): string | null {
  try {
    return sessionStorage.getItem(REGION_OVERRIDE_KEY);
  } catch {
    return null;
  }
}

export function GeoProvider({ children }: { children: ReactNode }) {
  const [detectedCountryCode, setDetectedCountryCode] = useState<string | null>(null);
  const [region, setRegion] = useState<string | null>(null);
  const [override, setOverride] = useState<string | null>(readStoredOverride);

  const setRegionOverride = (code: string | null) => {
    setOverride(code);
    try {
      if (code) {
        sessionStorage.setItem(REGION_OVERRIDE_KEY, code);
      } else {
        sessionStorage.removeItem(REGION_OVERRIDE_KEY);
      }
    } catch {
      // Session persistence is best-effort; in-memory override still applies.
    }
  };

  const countryCode = override ?? detectedCountryCode;

  useEffect(() => {
    const { promise, cancel } = geoJsService.getGeolocationData();

    promise
      .then(data => {
        setDetectedCountryCode(data.countryCode);
        setRegion(data.region);
      })
      .catch(err => {
        if ((err as { name?: string })?.name === 'AbortError') {
          return;
        }

        if (import.meta.env.DEV) {
          console.error('[Geo] Failed to detect location:', err);
        }
      });

    return cancel;
  }, []);

  return (
    <GeoContext.Provider
      value={{
        countryCode,
        detectedCountryCode,
        region,
        isRegionOverridden: override !== null,
        setRegionOverride,
      }}
    >
      {children}
    </GeoContext.Provider>
  );
}

export function useGeo(): GeoContextValue {
  const ctx = useContext(GeoContext);

  if (!ctx) {
    throw new Error('useGeo must be used inside GeoProvider');
  }

  return ctx;
}
